import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { UserService } from "../../services/UserService";
import { useTranslation } from "react-i18next";

const UserContext = createContext();

export default function UserProvider({ children }) {
  const [users, setUsers] = useState([]);
  const [errorSummary, setErrorSummary] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [user, setUser] = useState({
    active: true,
    roles: [],
    companyRoles: []
  });
  const [errors, setErros] = useState({});
  const [roles, setRolesList] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isUserListLoading, setIsUserListLoading] = useState(true);
  const [searchObject, setSearchObject] = useState({});
  const [superVisorList, setSuperVisorList] = useState([]);
  const pageSize = 20;
  const { t } = useTranslation();

  useEffect(() => {
    getUsers();
  }, [pageNumber]);

  useEffect(() => {
    getRoles();
    getSuperVisorList();
  }, []);

  function getSuperVisorList() {
    UserService.getSuperVisorList().then((_superVisorList) =>
      setSuperVisorList(_superVisorList)
    );
  }
  function getUsers() {
    UserService.getAllUsers(pageSize, pageNumber, searchObject)
      .then((res) => {
        if (res.users) setUsers(res.users);
        if (res.totalUsers <= pageSize) setTotalPages(1);
        else setTotalPages(Math.ceil(res.totalUsers / pageSize));
      })
      .catch((err) => {
        console.error(err);
        toast.error(t("Something went wrong."));
      })
      .finally(() => {
        setIsUserListLoading(false);
      });
  }

  function getUserById(userId) {
    UserService.getById(userId).then((_user) => {
      if (_user) setUser(_user);
    });
  }

  function getRoles() {
    UserService.getRoles().then((_roles) => setRolesList(_roles));
  }
  function openForm(userId) {
    getUserById(userId);
    setShowForm(true);
  }

  function onCloseForm() {
    setUser({ roles: [], companyRoles: [] });
    setErrorSummary("");
    setErros({});
    setShowForm(false);
  }

  function onOpenCreateUserForm() {
    setUser({ active: true, roles: [], companyRoles: [] });
    setShowForm(true);
  }

  function onChange({ target }) {
    if (target.name === "active") {
      if (user.active) setUser({ ...user, active: false });
      else setUser({ ...user, active: true });
    } else {
      setUser({ ...user, [target.name]: target.value });
    }
  }

  function onSubmitUser(event) {
    event.preventDefault();
    if (!validateUser()) return;

    UserService.createOrUpdate(user).then((res) => {
      if (res?.error) {
        return;
      }
      getUsers();
      onCloseForm();
      toast.success(t("User added successfully"));
    });
  }

  function validateUser() {
    const _erros = {};
    //validate for empty fields
    if (!user.userName) _erros.userName = t("The username is required.");
    if (!user.fullName) _erros.fullName = t("The Full Name is required.");
    if (!user.email) _erros.email = t("The Email Address is required.");
    if (!user.roles || user.roles.length === 0)
      _erros.roleId = t("The Profile is required.");

    let emailPatern = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
    if (!emailPatern.test(user.email))
      _erros.email = t("The Email Address format is invalid.");
    setErros(_erros);
    return Object.keys(_erros).length === 0;
  }
  function setRoles(list) {
    setUser({ ...user, roles: list });
  }

  function setCompanyRoles(list) {
    setUser({ ...user, companyRoles: list });
  }

  return (
    <UserContext.Provider
      value={{
        users,
        setUsers,
        getUsers,
        errorSummary,
        setErrorSummary,
        showForm,
        setShowForm,
        user,
        setUser,
        errors,
        setErros,
        openForm,
        onCloseForm,
        getUserById,
        onOpenCreateUserForm,
        onChange,
        onSubmitUser,
        roles,
        setPageNumber,
        pageNumber,
        totalPages,
        isUserListLoading,
        searchObject,
        setSearchObject,
        setRoles,
        setCompanyRoles,
        superVisorList
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext() {
  const context = useContext(UserContext);

  return context;
}
